import { Box, Flex, Heading, HStack, Stack, Tag, Text, Textarea } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type {
  ContributingEntityQualificationResponseDto,
  SelectionCriterionDto,
} from "../../../../autogen/bff-api";
import { StatusAccentBar } from "../../../../common/components/StatusAccentBar";
import { StatusDotLabel } from "../../../../common/components/StatusDotLabel";
import type { StatusVariant } from "../../../../common/components/StatusVariant";
import { composeCriterionKey } from "../../../../common/qualification/criterionKey";
import { TipTap, isTiptapContentEmpty } from "../../../../common/input/TipTap/TipTap";

interface Props {
  selectionCriteria: SelectionCriterionDto[];
  responses: ContributingEntityQualificationResponseDto[];
  onChange: (next: ContributingEntityQualificationResponseDto[]) => void;
  readOnly: boolean;
}

export const ContributingQualificationCard = ({
  selectionCriteria,
  responses,
  onChange,
  readOnly,
}: Props) => {
  const { t } = useTranslation();
  if (selectionCriteria.length === 0) return null;

  const answerFor = (criterion: SelectionCriterionDto) =>
    responses.find((r) => r.criterionKey === composeCriterionKey(criterion))?.description ?? "";

  const setAnswer = (criterion: SelectionCriterionDto, description: string) => {
    const key = composeCriterionKey(criterion);
    onChange([...responses.filter((r) => r.criterionKey !== key), { criterionKey: key, description }]);
  };

  const answeredCount = selectionCriteria.filter((c) => answerFor(c).trim().length > 0).length;

  return (
    <Box mb="8">
      <Flex align="center" justify="space-between" gap="3" mb="2" flexWrap="wrap">
        <HStack spacing="2" align="center" flexWrap="wrap">
          <Heading size="md">{t("Qualification requirements")}</Heading>
          <Tag size="sm" bg="smPrimarySofter" color="smPrimary" textTransform="none">
            {t("ESPD Part IV")}
          </Tag>
        </HStack>
        <Text
          fontSize="sm"
          fontWeight="semibold"
          color={answeredCount === selectionCriteria.length ? "smSuccess" : "smMuted"}
        >
          {t("{{answered}} of {{total}} answered", {
            answered: answeredCount,
            total: selectionCriteria.length,
          })}
        </Text>
      </Flex>
      <Text fontSize="sm" color="smMuted" mb="4">
        {t("The buyer requires the following of the suppliers taking part in this procurement.")}
      </Text>

      <Stack spacing="4">
        {selectionCriteria.map((criterion) => {
          const answer = answerFor(criterion);
          const variant: StatusVariant = answer.trim().length > 0 ? "ok" : "neutral";
          return (
            <Box
              key={composeCriterionKey(criterion)}
              position="relative"
              overflow="hidden"
              borderWidth="1px"
              borderColor="smBorder"
              rounded="lg"
              bg="smBackground"
            >
              <StatusAccentBar variant={variant} />
              <Box px="4" py="3">
                <Stack spacing="3">
                  <HStack spacing="2" align="center" flexWrap="wrap">
                    <Tag size="sm" colorScheme="gray" variant="subtle">
                      {t(criterion.type)}
                    </Tag>
                    <Heading size="sm">{criterion.name}</Heading>
                  </HStack>

                  {!isTiptapContentEmpty(criterion.description) && (
                    <Box fontSize="sm" color="smMuted">
                      <TipTap
                        content={criterion.description}
                        editable={false}
                        hasBorder={false}
                        padding="0"
                        maxHeight=""
                      />
                    </Box>
                  )}

                  <Box borderWidth="1px" borderRadius="md" p="4">
                    <Flex mb="2" justify="space-between" align="center" gap="2">
                      <Text fontSize="sm" fontWeight="medium">
                        {t("Your answer?")}
                      </Text>
                      <StatusDotLabel
                        variant={variant}
                        label={variant === "ok" ? t("Answered") : t("Not answered")}
                      />
                    </Flex>
                    <Textarea
                      rows={3}
                      isReadOnly={readOnly}
                      value={answer}
                      placeholder={String(t("Answer the qualification requirement"))}
                      onChange={(e) => setAnswer(criterion, e.target.value)}
                    />
                  </Box>
                </Stack>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
